import React, { useState } from "react";
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  Heading1, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  ListTodo, 
  Quote, 
  Code, 
  SquareCode, 
  Table, 
  Minus, 
  Image as ImageIcon, 
  Video, 
  Link as LinkIcon, 
  Sparkles, 
  Info, 
  AlertTriangle, 
  Bell,
  FileText,
  Wand2,
  Target,
  Upload,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { UnsplashStockGalleryModal } from "./UnsplashStockGalleryModal";
import { useApiClient } from "@/hooks/use-api-client";

interface RichEditorToolbarProps {
  content: string;
  onChange: (newContent: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onApplyTemplate?: (templateType: "calculadoras" | "release" | "tutorial" | "marketing" | "ingesta_masiva") => void;
}

// Preset cover images for quick selection
const IMAGE_PRESETS = [
  {
    title: "Calculadoras y Finanzas",
    url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop"
  },
  {
    title: "Justicia y Leyes",
    url: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop"
  },
  {
    title: "Tecnología e IA",
    url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop"
  },
  {
    title: "Documentos y Contratos",
    url: "https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=1200&auto=format&fit=crop"
  },
  {
    title: "Despachos Judiciales",
    url: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1200&auto=format&fit=crop"
  }
];

// Preset video URLs for quick selection
const VIDEO_PRESETS = [
  {
    title: "Tutorial: Demostración de Calculadoras Jurídicas",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
  },
  {
    title: "Guía de Inicio Rápido en Mi Asistente Legal IA",
    url: "https://www.youtube.com/watch?v=L_LUpnjgPso"
  }
];

export function RichEditorToolbar({
  content,
  onChange,
  textareaRef,
  onApplyTemplate
}: RichEditorToolbarProps) {
  const apiClient = useApiClient();
  const [uploadingImage, setUploadingImage] = useState(false);

  // Dialog states
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isStockGalleryOpen, setIsStockGalleryOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageCaption, setImageCaption] = useState("");

  const handleFileUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiClient.post("/api/v1/bulletins/upload-image", formData);
      if (res.ok && res.data?.url) {
        setImageUrl(res.data.url);
        if (!imageCaption) setImageCaption(file.name.replace(/\.[^/.]+$/, ""));
        toast.success("¡Imagen subida correctamente al servidor!");
      } else {
        toast.error("Error al subir la imagen al servidor.");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      toast.error("Error de conexión al subir la imagen.");
    } finally {
      setUploadingImage(false);
    }
  };

  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const [isCalloutDialogOpen, setIsCalloutDialogOpen] = useState(false);
  const [calloutType, setCalloutType] = useState<"NOTE" | "TIP" | "WARNING" | "IMPORTANT">("TIP");
  const [calloutText, setCalloutText] = useState("");

  // Helper to insert markdown text at cursor or wrap current selection
  const insertFormatting = (prefix: string, suffix: string = "", defaultText: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange(content + `${prefix}${defaultText}${suffix}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end) || defaultText;

    const newText = content.substring(0, start) + prefix + selectedText + suffix + content.substring(end);
    onChange(newText);

    // Reset selection focus
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + selectedText.length
      );
    }, 50);
  };

  // Helper to insert a block at start of current line or new line
  const insertBlock = (blockText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange(content + "\n\n" + blockText + "\n\n");
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const needsLeadingNewline = start > 0 && content[start - 1] !== "\n";
    const prefix = needsLeadingNewline ? "\n\n" : "";

    const newText = content.substring(0, start) + prefix + blockText + "\n\n" + content.substring(end);
    onChange(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + blockText.length + 2;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 50);
  };

  // Handlers for Dialog insertions
  const handleInsertImage = () => {
    if (!imageUrl) {
      toast.warning("Por favor ingresa una URL de imagen válida.");
      return;
    }
    const caption = imageCaption.trim() || "Imagen explicativa";
    insertBlock(`![${caption}](${imageUrl})`);
    setIsImageDialogOpen(false);
    setImageUrl("");
    setImageCaption("");
    toast.success("Imagen insertada correctamente.");
  };

  const handleInsertVideo = () => {
    if (!videoUrl) {
      toast.warning("Por favor ingresa la URL del vídeo.");
      return;
    }
    const title = videoTitle.trim() || "Vídeo Tutorial Interactivo";
    insertBlock(`[${title}](${videoUrl})`);
    setIsVideoDialogOpen(false);
    setVideoUrl("");
    setVideoTitle("");
    toast.success("Vídeo insertado correctamente.");
  };

  const handleInsertLink = () => {
    if (!linkUrl) {
      toast.warning("Ingresa una URL válida.");
      return;
    }
    const text = linkText.trim() || linkUrl;
    insertFormatting(`[${text}](`, `)`, linkUrl);
    setIsLinkDialogOpen(false);
    setLinkText("");
    setLinkUrl("");
  };

  const handleInsertCallout = () => {
    if (!calloutText) {
      toast.warning("Escribe el texto del cuadro destacado.");
      return;
    }
    insertBlock(`> [!${calloutType}]\n> ${calloutText}`);
    setIsCalloutDialogOpen(false);
    setCalloutText("");
    toast.success("Cuadro destacado insertado.");
  };

  const handleInsertTable = () => {
    const tableMd = `| Característica / Función | Método Tradicional | Nueva Función Legal AI |
| :--- | :--- | :--- |
| **Tiempo de Cálculo** | 45-60 Minutos manual | **Menos de 2 Segundos** ⚡ |
| **Actualización IPC** | Búsqueda manual de listas DANE | **Automático con API en tiempo real** |
| **Intereses Moratorios** | Cálculos complejos en Excel | **Certificado de Superfinanciera** |
| **Exportación** | Manual en Word | **Reporte PDF / Excel Oficial** |`;

    insertBlock(tableMd);
    toast.success("Tabla de formato insertada.");
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm mb-3">
      {/* Group 1: Basic Text Formats */}
      <div className="flex items-center gap-1 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertFormatting("**", "**", "texto en negrita")}
          title="Negrita (Ctrl+B)"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertFormatting("*", "*", "texto en cursiva")}
          title="Cursiva (Ctrl+I)"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertFormatting("~~", "~~", "texto tachado")}
          title="Tachado"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Strikethrough className="w-4 h-4" />
        </Button>

        <div className="w-px h-5 bg-slate-300 dark:bg-slate-700 mx-1" />

        {/* Headings */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("# Título Principal")}
          title="Título H1"
          className="h-8 px-2 rounded-lg hover:bg-white dark:hover:bg-slate-900 font-bold text-xs text-slate-700 dark:text-slate-200 flex items-center gap-1"
        >
          <Heading1 className="w-4 h-4 text-pink-600" /> H1
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("## Subtítulo Destacado")}
          title="Título H2"
          className="h-8 px-2 rounded-lg hover:bg-white dark:hover:bg-slate-900 font-bold text-xs text-slate-700 dark:text-slate-200 flex items-center gap-1"
        >
          <Heading2 className="w-4 h-4 text-pink-600" /> H2
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("### Encabezado Sección")}
          title="Título H3"
          className="h-8 px-2 rounded-lg hover:bg-white dark:hover:bg-slate-900 font-bold text-xs text-slate-700 dark:text-slate-200 flex items-center gap-1"
        >
          <Heading3 className="w-4 h-4 text-pink-600" /> H3
        </Button>

        <div className="w-px h-5 bg-slate-300 dark:bg-slate-700 mx-1" />

        {/* Lists & Quotes */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("- Primer elemento de lista\n- Segundo elemento\n- Tercer elemento")}
          title="Lista con viñetas"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("1. Paso uno\n2. Paso dos\n3. Paso tres")}
          title="Lista numerada"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("- [ ] Tarea por realizar\n- [x] Tarea completada")}
          title="Lista de verificación"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <ListTodo className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("> Cita legal o frase importante extraída.")}
          title="Bloque de Cita"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Quote className="w-4 h-4" />
        </Button>

        <div className="w-px h-5 bg-slate-300 dark:bg-slate-700 mx-1" />

        {/* Code & Table */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertFormatting("`", "`", "variable_o_codigo")}
          title="Código en línea"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Code className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("```python\n# Ejemplo de código\ndef calcular_intereses(monto, dtf):\n    return monto * (dtf / 100)\n```")}
          title="Bloque de Código completo"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <SquareCode className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleInsertTable}
          title="Insertar Tabla Markdown"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Table className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertBlock("---")}
          title="Línea Divisoria"
          className="h-8 w-8 p-0 rounded-lg hover:bg-white dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <Minus className="w-4 h-4" />
        </Button>
      </div>

      {/* Group 2: Rich Media & Callout Modals & Templates */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsImageDialogOpen(true)}
          className="h-8 px-2.5 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold text-xs hover:border-pink-500 hover:text-pink-600 flex items-center gap-1.5 shadow-2xs"
        >
          <ImageIcon className="w-3.5 h-3.5 text-pink-500" />
          + Imagen
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsVideoDialogOpen(true)}
          className="h-8 px-2.5 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold text-xs hover:border-pink-500 hover:text-pink-600 flex items-center gap-1.5 shadow-2xs"
        >
          <Video className="w-3.5 h-3.5 text-pink-500" />
          + Vídeo
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsCalloutDialogOpen(true)}
          className="h-8 px-2.5 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold text-xs hover:border-pink-500 hover:text-pink-600 flex items-center gap-1.5 shadow-2xs"
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          + Destacado
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsLinkDialogOpen(true)}
          className="h-8 px-2.5 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold text-xs hover:border-pink-500 hover:text-pink-600 flex items-center gap-1.5 shadow-2xs"
        >
          <LinkIcon className="w-3.5 h-3.5 text-blue-500" />
          Enlace
        </Button>

        {/* Quick Template Dropdown */}
        {onApplyTemplate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                className="h-8 px-3 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold text-xs shadow-md hover:from-pink-700 hover:to-rose-700 flex items-center gap-1.5"
              >
                <Wand2 className="w-3.5 h-3.5" />
                Plantilla Rápida
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 rounded-2xl p-2 shadow-xl border-slate-200 dark:border-slate-800">
              <DropdownMenuItem 
                onClick={() => onApplyTemplate("calculadoras")}
                className="rounded-xl p-3 cursor-pointer hover:bg-pink-50 dark:hover:bg-pink-950/30 flex flex-col items-start gap-1"
              >
                <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs">
                  <Sparkles className="w-4 h-4 text-pink-500" />
                  Post: Lanzamiento Calculadoras
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Carga el post oficial completo con imágenes, vídeo y tablas.
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem 
                onClick={() => onApplyTemplate("ingesta_masiva")}
                className="rounded-xl p-3 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950/30 flex flex-col items-start gap-1 mt-1"
              >
                <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs">
                  <Upload className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Post: Ingesta Masiva de Procesos
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Post explicativo sobre la carga masiva de expedientes (.xlsx/.csv).
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem 
                onClick={() => onApplyTemplate("release")}
                className="rounded-xl p-3 cursor-pointer hover:bg-pink-50 dark:hover:bg-pink-950/30 flex flex-col items-start gap-1 mt-1"
              >
                <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Plantilla: Release Notes
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Estructura para actualizaciones de sistema y correcciones.
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem 
                onClick={() => onApplyTemplate("marketing")}
                className="rounded-xl p-3 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-950/30 flex flex-col items-start gap-1 mt-1"
              >
                <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs">
                  <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  Plantilla: Marketing & Captación de Leads
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Post persuasivo enfocado en conversión y prueba gratuita para prospectos.
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem 
                onClick={() => onApplyTemplate("tutorial")}
                className="rounded-xl p-3 cursor-pointer hover:bg-pink-50 dark:hover:bg-pink-950/30 flex flex-col items-start gap-1 mt-1"
              >
                <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs">
                  <Video className="w-4 h-4 text-emerald-500" />
                  Plantilla: Tutorial Paso a Paso
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Formato de guía paso a paso con bloques de vídeo y consejos.
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* MODAL 1: INSERT IMAGE */}
      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-[2rem] border-none shadow-2xl p-6 bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-slate-900 dark:text-white">
              <ImageIcon className="w-5 h-5 text-pink-500" />
              Insertar Imagen en el Boletín
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pega el enlace de una imagen o selecciona una portada prediseñada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            {/* File Upload Option */}
            <div className="p-4 rounded-2xl bg-pink-50/50 dark:bg-pink-950/20 border-2 border-dashed border-pink-200 dark:border-pink-800/50 text-center space-y-2">
              <span className="text-xs font-bold text-pink-900 dark:text-pink-300 block">
                📁 Cargar desde tu Dispositivo o Pegar
              </span>
              <p className="text-[11px] text-pink-700 dark:text-pink-400">
                Selecciona cualquier imagen (.png, .jpg, .webp, .gif) de tu computador.
              </p>
              <input
                type="file"
                accept="image/*"
                id="toolbar-image-file-input"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingImage}
                onClick={() => document.getElementById("toolbar-image-file-input")?.click()}
                className="rounded-xl border-pink-300 dark:border-pink-700 bg-white dark:bg-slate-900 text-pink-600 dark:text-pink-400 font-bold text-xs hover:bg-pink-100"
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin text-pink-500" />
                    Subiendo a Servidor...
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5 mr-1.5 text-pink-500" />
                    Examinar Archivos
                  </>
                )}
              </Button>
            </div>

            <div className="relative flex py-1 items-center">
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
              <span className="flex-shrink mx-3 text-[10px] font-extrabold uppercase text-slate-400">O pegar enlace web / seleccionar galería</span>
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">URL o Data de la Imagen *</label>
              <Input
                placeholder="https://images.unsplash.com/photo-... o data:image/..."
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="rounded-xl font-mono text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Leyenda / Pie de foto</label>
              <Input
                placeholder="Ej. Interfaz del Módulo de Calculadoras Jurídicas"
                value={imageCaption}
                onChange={(e) => setImageCaption(e.target.value)}
                className="rounded-xl"
              />
            </div>

            {/* Presets Gallery */}
            <div className="space-y-2 pt-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                Seleccionar Imagen de Galería Destacada
              </span>
              <div className="grid grid-cols-3 gap-2">
                {IMAGE_PRESETS.map((preset) => (
                  <div
                    key={preset.title}
                    onClick={() => {
                      setImageUrl(preset.url);
                      setImageCaption(preset.title);
                    }}
                    className={`group relative h-20 rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                      imageUrl === preset.url ? "border-pink-500 shadow-md scale-102" : "border-transparent opacity-80 hover:opacity-100"
                    }`}
                  >
                    <img src={preset.url} alt={preset.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 p-1 flex items-end">
                      <span className="text-[10px] font-bold text-white leading-tight line-clamp-1">
                        {preset.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsImageDialogOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleInsertImage} className="bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl px-6">
              Insertar Imagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 2: INSERT VIDEO */}
      <Dialog open={isVideoDialogOpen} onOpenChange={setIsVideoDialogOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-[2rem] border-none shadow-2xl p-6 bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-slate-900 dark:text-white">
              <Video className="w-5 h-5 text-pink-500" />
              Insertar Vídeo Interactivo
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Soporta vídeos de YouTube, Vimeo, Loom o archivos MP4 directos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">URL del Vídeo *</label>
              <Input
                placeholder="Ej. https://www.youtube.com/watch?v=... o https://vimeo.com/..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Título / Descripción del Vídeo</label>
              <Input
                placeholder="Ej. Tutorial Paso a Paso: Cómo calcular liquidación de daños"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                className="rounded-xl"
              />
            </div>

            {/* Video Presets */}
            <div className="space-y-2 pt-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                Vídeos de Prueba Sugeridos
              </span>
              <div className="space-y-2">
                {VIDEO_PRESETS.map((vp) => (
                  <div
                    key={vp.title}
                    onClick={() => {
                      setVideoUrl(vp.url);
                      setVideoTitle(vp.title);
                    }}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between text-xs font-bold ${
                      videoUrl === vp.url 
                        ? "border-pink-500 bg-pink-50 dark:bg-pink-950/30 text-pink-600 dark:text-pink-400" 
                        : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-850"
                    }`}
                  >
                    <span>{vp.title}</span>
                    {videoUrl === vp.url && <Check className="w-4 h-4 text-pink-600" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsVideoDialogOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleInsertVideo} className="bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl px-6">
              Insertar Vídeo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 3: INSERT CALLOUT */}
      <Dialog open={isCalloutDialogOpen} onOpenChange={setIsCalloutDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-[2rem] border-none shadow-2xl p-6 bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-slate-900 dark:text-white">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Insertar Cuadro Destacado (Callout)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Tipo de Destacado</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={calloutType === "TIP" ? "default" : "outline"}
                  onClick={() => setCalloutType("TIP")}
                  className={`rounded-xl justify-start text-xs font-bold ${calloutType === "TIP" ? "bg-emerald-600 text-white" : ""}`}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-emerald-400" /> Consejo Pro
                </Button>
                <Button
                  type="button"
                  variant={calloutType === "IMPORTANT" ? "default" : "outline"}
                  onClick={() => setCalloutType("IMPORTANT")}
                  className={`rounded-xl justify-start text-xs font-bold ${calloutType === "IMPORTANT" ? "bg-purple-600 text-white" : ""}`}
                >
                  <Bell className="w-4 h-4 mr-2 text-purple-400" /> Importante
                </Button>
                <Button
                  type="button"
                  variant={calloutType === "NOTE" ? "default" : "outline"}
                  onClick={() => setCalloutType("NOTE")}
                  className={`rounded-xl justify-start text-xs font-bold ${calloutType === "NOTE" ? "bg-blue-600 text-white" : ""}`}
                >
                  <Info className="w-4 h-4 mr-2 text-blue-400" /> Nota Informativa
                </Button>
                <Button
                  type="button"
                  variant={calloutType === "WARNING" ? "default" : "outline"}
                  onClick={() => setCalloutType("WARNING")}
                  className={`rounded-xl justify-start text-xs font-bold ${calloutType === "WARNING" ? "bg-amber-600 text-white" : ""}`}
                >
                  <AlertTriangle className="w-4 h-4 mr-2 text-amber-400" /> Advertencia
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Contenido del Mensaje *</label>
              <Input
                placeholder="Escribe el mensaje destacado..."
                value={calloutText}
                onChange={(e) => setCalloutText(e.target.value)}
                className="rounded-xl"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsCalloutDialogOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleInsertCallout} className="bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl px-6">
              Insertar Bloque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 4: INSERT LINK */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent className="sm:max-w-[450px] rounded-[2rem] border-none shadow-2xl p-6 bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-slate-900 dark:text-white">
              <LinkIcon className="w-5 h-5 text-blue-500" />
              Insertar Enlace
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Texto Ancla</label>
              <Input
                placeholder="Ej. Ir al Módulo de Calculadoras"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">URL Destino *</label>
              <Input
                placeholder="https://... o /dashboard/calculadoras"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="rounded-xl"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsLinkDialogOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleInsertLink} className="bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl px-6">
              Insertar Enlace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsplashStockGalleryModal
        isOpen={isStockGalleryOpen}
        onClose={() => setIsStockGalleryOpen(false)}
        onInsertMarkdownImage={(url, title) => {
          insertFormatting(`![${title}](`, ")", url);
        }}
      />
    </div>
  );
}
